import { Box, SxProps, Typography } from "@outschool/backpack";
import {
  REFER_CREDIT_CENTS,
  REFER_TEACHER_CREDIT_CENTS,
  REFER_TEACHER_CREDIT_REQ_CENTS,
} from "@outschool/business-rules";
import {
  ParentReferralStatus,
  TeacherReferralStatus,
} from "@outschool/gql-backend-generated";
import {
  OutreachReferralStatus,
  ReferralsQuery,
  ReferralsQueryVariables,
} from "@outschool/gql-frontend-generated";
import {
  TFunction,
  Trans,
  renderCurrencyString,
  useTranslation,
} from "@outschool/localization";
import { SupportRoutes } from "@outschool/routes";
import { Query, QueryResult, gql } from "@outschool/ui-apollo";
import { ExternalLink } from "@outschool/ui-components-shared";
import { Container } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React, { ReactNode } from "react";

import * as Env from "../../shared/Env";
import ErrorMessage from "./ErrorMessage";

const PARENT_CREDIT = renderCurrencyString({
  priceInCents: REFER_CREDIT_CENTS,
});
const TEACHER_CREDIT = renderCurrencyString({
  priceInCents: REFER_TEACHER_CREDIT_CENTS,
});
const TEACHER_CREDIT_REQUIREMENT = renderCurrencyString({
  priceInCents: REFER_TEACHER_CREDIT_REQ_CENTS,
});

export const UserReferrals = gql`
  query Referrals {
    currentUser {
      uid
      totalReferralEarnings
      referredParents {
        firstName
        status
        claimedAmountCents
      }
      referredTeachers {
        firstName
        status
      }
    }
  }
`;

export const OutreachUserReferrals = gql`
  query OutreachReferrals($after: String) {
    currentUser {
      uid
      outreachReferrals(first: 20, after: $after) {
        edges {
          node {
            firstName
            status
          }
        }
        pageInfo {
          hasNextPage
          lastCursor
        }
      }
    }
  }
`;

function referredTeacherStatusToLabel(
  status: TeacherReferralStatus,
  t: TFunction,
  _amountCents?: number,
): string {
  switch (status) {
    case TeacherReferralStatus.ApplicationDeclined:
      return t("Application Declined");
    case TeacherReferralStatus.EligibleForPayOut:
      return t("Eligible For Pay Out");
    case TeacherReferralStatus.TeacherApplied:
      return t("Signed Up, Not Yet Approved");
    case TeacherReferralStatus.TeacherApproved:
      return t("Teacher Approved");
    case TeacherReferralStatus.PaidOut:
      return t("Paid Out");
  }
}

function referredParentStatusToLabel(
  status: ParentReferralStatus,
  t: TFunction,
  _amountCents?: number,
): ReactNode {
  switch (status) {
    case ParentReferralStatus.CreditClaimed:
      return t("Credit Claimed");
    case ParentReferralStatus.CreditEarned:
      return t("Credit Earned");
    case ParentReferralStatus.SignedUp:
      return t("Signed Up");
  }
}

export const referredOutreachParentStatusToLabel = (
  status: OutreachReferralStatus,
  t: TFunction,
  _amountCents?: number,
): ReactNode => {
  switch (status) {
    case OutreachReferralStatus.SignedUp:
      return <>{t`Signed Up`}</>;
    case OutreachReferralStatus.MembershipStarted:
      return <>{t`Membership Started`}</>;
    case OutreachReferralStatus.MembershipRenewed:
      return <>{t`Membership Renewed`}</>;
    default:
      return <>{t`Unknown`}</>;
  }
};

function ParentHowItWorks({ t }: { t: TFunction }) {
  return (
    <>
      <Typography variant="h3">{t`Parents`}</Typography>
      <Box>
        <ol type="1">
          <li>{t`Share your link on social media, text, or email.`}</li>
          <li>
            <Trans t={t}>
              Someone you know signs up and automatically gets{" "}
              <strong>{{ parentCredit: PARENT_CREDIT }}</strong> credit applied
              to their first class.
            </Trans>
          </li>
          <li>
            <Trans t={t}>
              Once they start the class, you’ll get a{" "}
              <strong>{{ parentCredit: PARENT_CREDIT }}</strong> credit
              automatically applied towards your next Outschool class.{" "}
              <ExternalLink
                url={SupportRoutes.supportParentReferralProgramUrl()}
              >
                Learn more
              </ExternalLink>{" "}
              in our help center.
            </Trans>
          </li>
        </ol>
      </Box>
    </>
  );
}

export function ReferredParentsList({
  referredParents,
  t,
  omitTitle,
  sx = {},
  makeLabel = referredParentStatusToLabel,
}: {
  // @ts-ignore TS(2339): Property 'referredParents' does not exist on type ... Remove this comment to see the full error message
  referredParents: ReferralsQuery["currentUser"]["referredParents"];
  t: TFunction;
  omitTitle?: boolean;
  sx?: SxProps;
  makeLabel?: (status: any, t: TFunction, amountCents: number) => ReactNode;
}) {
  // filter parents with no first name
  referredParents = referredParents.filter(
    (rp: $TSFixMe) => !!rp?.firstName && rp?.firstName?.length > 0,
  );
  return (
    <>
      {!omitTitle && (
        <Typography variant="h3">{t`Your parent referrals`}</Typography>
      )}
      {referredParents.length > 0 ? (
        <Box
          component="table"
          sx={[
            {
              borderCollapse: "collapse",

              "tbody tr": {
                borderBottom: "1px solid #DDDFE3",
              },
            },
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
        >
          <thead>
            <tr>
              <th>{t`Name`}</th>
              <th>{t`Status`}</th>
            </tr>
          </thead>
          <tbody>
            {referredParents.map((p: $TSFixMe, i: $TSFixMe) => (
              <tr key={i}>
                <td>{p.firstName}</td>
                <td>
                  {makeLabel(
                    p.status,
                    t,
                    p.claimedAmountCents ? p.claimedAmountCents : undefined,
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Box>
      ) : (
        <Box>
          {t`Your parent referrals will show up here once they sign up via your special link.`}
        </Box>
      )}
    </>
  );
}

function TeacherHowItWorks({ t }: { t: TFunction }) {
  return (
    <>
      <Typography variant="h3">{t`Teachers`}</Typography>
      <Box>
        <ol type="1">
          <li>{t`Share your link on social media, text, or email.`}</li>
          <li>{t`Someone you know signs up as a teacher.`}</li>
          <li>
            <Trans t={t}>
              Once they earn their first{" "}
              <strong>
                {{ teacherCreditRequirement: TEACHER_CREDIT_REQUIREMENT }}
              </strong>
              , you’ll get <strong>{{ teacherCredit: TEACHER_CREDIT }}</strong>{" "}
              cash.{" "}
              <ExternalLink
                url={SupportRoutes.supportTeacherReferralProgramUrl()}
              >
                Learn more
              </ExternalLink>{" "}
              in our help center.
            </Trans>
          </li>
        </ol>
      </Box>
    </>
  );
}

function ReferredTeachersList({
  referredTeachers,
  t,
}: {
  // @ts-ignore TS(2339): Property 'referredTeachers' does not exist on type... Remove this comment to see the full error message
  referredTeachers: ReferralsQuery["currentUser"]["referredTeachers"];
  t: TFunction;
}) {
  return (
    <>
      <Typography variant="h3">{t`Your teachers referrals`}</Typography>
      {referredTeachers.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>{t`Name`}</th>
              <th>{t`Status`}</th>
            </tr>
          </thead>
          <tbody>
            {referredTeachers.map((p: $TSFixMe, i: $TSFixMe) => (
              <tr key={i}>
                <td>{p.firstName}</td>
                <td>{referredTeacherStatusToLabel(p.status, t)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Box>
          {t`Your teacher referrals will show up here once they sign up via your special link.`}
        </Box>
      )}
    </>
  );
}

export default function Referrals() {
  const { t } = useTranslation("client\\components\\Referrals");
  const isMobile = useIsMobile();
  return (
    <Query query={UserReferrals}>
      {({
        loading,
        error,
        data,
      }: QueryResult<ReferralsQuery, ReferralsQueryVariables>) => {
        if (loading) {
          return null;
        }
        if (error) {
          return (
            <ErrorMessage
              value={error}
              showStatusPageLink={Env.IS_READ_ONLY_MODE}
            />
          );
        }
        const {
          // @ts-ignore TS(2339): Property 'currentUser' does not exist on type 'Ref... Remove this comment to see the full error message
          currentUser: { referredParents, referredTeachers },
        } = data;
        return (
          <Container sx={{ marginBottom: "large" }}>
            <hr />
            <Typography variant="h2">{t`HOW IT WORKS:`}</Typography>
            {!isMobile ? (
              <>
                <Box flex>
                  <Box
                    sx={{
                      flex: 1,
                      margin: "16px 16px 0px 16px",
                    }}
                  >
                    <ParentHowItWorks t={t} />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      margin: "16px 16px 0px 16px",
                    }}
                  >
                    <TeacherHowItWorks t={t} />
                  </Box>
                </Box>
                <Box flex>
                  <Box
                    sx={{
                      flex: 1,
                      margin: "0px 16px 0px 16px",
                    }}
                  >
                    <ReferredParentsList
                      referredParents={referredParents}
                      t={t}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      margin: "0px 16px 0px 16px",
                    }}
                  >
                    <ReferredTeachersList
                      referredTeachers={referredTeachers}
                      t={t}
                    />
                  </Box>
                </Box>
              </>
            ) : (
              <Box
                flex
                sx={{
                  flexDirection: "column",
                }}
              >
                <Box
                  sx={{
                    margin: "16px 16px 0px 16px",
                  }}
                >
                  <ParentHowItWorks t={t} />
                </Box>
                <Box
                  sx={{
                    margin: "0px 16px 16px 16px",
                  }}
                >
                  <ReferredParentsList
                    referredParents={referredParents}
                    t={t}
                  />
                </Box>
                <Box
                  sx={{
                    margin: "16px 16px 0px 16px",
                  }}
                >
                  <TeacherHowItWorks t={t} />
                </Box>
                <Box
                  sx={{
                    margin: "0px 16px 16px 16px",
                  }}
                >
                  <ReferredTeachersList
                    referredTeachers={referredTeachers}
                    t={t}
                  />
                </Box>
              </Box>
            )}
          </Container>
        );
      }}
    </Query>
  );
}
